import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { View, Text, TouchableOpacity, TextInput, StyleSheet, Animated } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useApi } from "../context/ApiContext";
import { useData } from "../context/DataContext";
const AppleButton = ({ text = "Continue" }) => {
	return (
		<View style={styles.appleButtonContainer}>
			<TouchableOpacity style={styles.appleButton}>
				<Text style={styles.appleButtonText}>{text}</Text>
			</TouchableOpacity>
		</View>
	);
};
const PaymentsService = ({ onUpdate, app }) => {
	const { appId } = useParams();
	const { api } = useApi();
	const { refetchData, appData, teamData, loading } = useData();
	const [error, setError] = useState(null);
	const [appConfig, setAppConfig] = useState(null);
	const [showAppleConfigModal, setShowAppleConfigModal] = useState(false);
	const [showErrorDialog, setShowErrorDialog] = useState(false);
	const [currentStep, setCurrentStep] = useState(5);
	const [appleConfigData, setAppleConfigData] = useState({
		keyId: "",
		p8Key: "",
		p8FileName: "",
		stripeCertApplePay: "",
		stripeCertFileName: "",
		merchantId: "",
		merchantIdentifier: "",
		merchantName: "",
	});
	const [editingTestKey, setEditingTestKey] = useState(false);
	const [editingLiveKey, setEditingLiveKey] = useState(false);
	const [configSaving, setConfigSaving] = useState(false);
	const [teams, setTeams] = useState([]);
	const [selectedTeam, setSelectedTeam] = useState(null);
	const spinValue = new Animated.Value(0);
	const [downloadingCsr, setDownloadingCsr] = useState(false);
	const [merchantIds, setMerchantIds] = useState([]);
	const [selectedMerchantId, setSelectedMerchantId] = useState(null);
	const [fetchingMerchantIds, setFetchingMerchantIds] = useState(false);
	const [merchantIdError, setMerchantIdError] = useState(null);
	const [creatingMerchantId, setCreatingMerchantId] = useState(false);
	// Add Stripe Connect state variables
	const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
	const [fetchingConnectURL, setFetchingConnectURL] = useState(false);
	const [stripeConnectError, setStripeConnectError] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);
	const [stripeConnectData, setStripeConnectData] = useState({
		accountId: "",
		status: "not_connected",
		lastUpdated: null,
		dashboard_display_name: "",
		tenantId: null,
		publishableKeyTest: "",
		publishableKeyLive: "",
	});

	// Create the spinning animation
	useEffect(() => {
		if (configSaving) {
			Animated.loop(
				Animated.timing(spinValue, {
					toValue: 1,
					duration: 1000,
					useNativeDriver: true,
				})
			).start();
		} else {
			spinValue.setValue(0);
		}
	}, [configSaving]);

	useEffect(() => {
		fetchConfig();
	}, [appId]);

	// Fetch teams on component mount
	useEffect(() => {
		fetchTeams();
	}, []);

	// Reset step when modal is opened
	useEffect(() => {
		if (showAppleConfigModal) {
			setCurrentStep(1);
		}
	}, [showAppleConfigModal]);

	// Add effect to handle team selection when teams are loaded
	useEffect(() => {
		if (teams.length > 0 && appConfig?.teamId) {
			const savedTeam = teams.find((team) => team.id === appConfig.teamId);
			if (savedTeam) {
				setSelectedTeam(savedTeam);
			}
		}
	}, [teams, appConfig?.teamId]);

	// Add useEffect to fetch merchant IDs when the step changes to 3
	useEffect(() => {
		if (currentStep === 3 && selectedTeam && !merchantIds.length && !fetchingMerchantIds) {
			fetchMerchantIds();
		}
	}, [currentStep, selectedTeam]);

	// Add effect to fetch Stripe Connect status on component mount
	useEffect(() => {
		if (appData?.services) {
			const paymentsService = appData?.services?.find((service) => service.type === "payments");
			if (paymentsService?.config?.stripeConnect) {
				setStripeConnectData({
					accountId: paymentsService.config.stripeConnect.accountId || "",
					status: paymentsService.config.stripeConnect.status || "not_connected",
					lastUpdated: paymentsService.config.stripeConnect.lastUpdated || null,
					dashboard_display_name: paymentsService.config.stripeConnect.dashboard_display_name || "",
				});
			}
		}
	}, [appData]);

	// Add useEffect to update the keys when selectedTeam changes
	useEffect(() => {
		if (selectedTeam && selectedTeam.id) {
			fetchApplePayKeys(selectedTeam.id);
		}
	}, [selectedTeam]);

	// Add a function to fetch the Apple Pay publishable keys
	const fetchApplePayKeys = async (teamId) => {
		try {
			const response = await api("/team", {
				method: "POST",
				body: {
					action: "getApplePayKeys",
					teamId: teamId,
				},
			});

			if (response) {
				setStripeConnectData((prev) => ({
					...prev,
					publishableKeyTest: response.publishableKeyTest || "",
					publishableKeyLive: response.publishableKeyLive || "",
				}));
			}
		} catch (err) {
			console.error("Error fetching Apple Pay keys:", err);
		}
	};

	const fetchConfig = async () => {
		try {
			const response = appData;

			// Find the push_notifications service in the services array
			const paymentsService = response?.services?.find((service) => service.type === "payments");
			const serviceConfig = paymentsService?.config || {};
			const serviceStatus = paymentsService?.status || "inactive";
			// Check if Apple Core Service is active
			const appleCoreService = response?.services?.find((service) => service.type === "apple_developer");
			const isAppleCoreActive = appleCoreService?.status === "active"; //necessary for the apple config modal to work
			const teamId = serviceConfig.teamId || appleCoreService?.config?.teamId || null;
			// Set app config with either service data or defaults
			setAppConfig({
				name: serviceConfig.appName || response?.name || "",
				bundleId: serviceConfig.bundleId || "",
				teamId: teamId,
				isAppleCoreActive: isAppleCoreActive,
				serviceStatus: serviceStatus,
				tenantId: response?.services?.find((service) => service.type === "accounts")?.config?.realmId || null,
			});

			setError(null);
		} catch (err) {
			console.error("Error fetching app config:", err);
			setError(err.message);
		}
	};

	const fetchTeams = async () => {
		try {
			const response = teamData;

			// The response includes teams with service status information
			// Each team has: id, name, description, role, services, createdAt, updatedAt
			const updatedTeams = response.map((team) => ({
				...team,
				services: team.services || {},
				role: team.role || "member",
			}));
			setTeams(updatedTeams);

			setError(null);
		} catch (err) {
			setError(err.message);
		}
	};

	const fetchMerchantIds = async () => {
		if (!selectedTeam) return;

		setFetchingMerchantIds(true);
		setMerchantIdError(null);

		try {
			const response = await api("/app", {
				method: "POST",
				body: {
					action: "fetch_merchant_ids",
					teamId: selectedTeam.id,
				},
			});

			if (response && response.merchantIds && response.merchantIds.length > 0) {
				setMerchantIds(response.merchantIds);
				// If there's only one, select it automatically
				if (response.merchantIds.length === 1) {
					setSelectedMerchantId(response.merchantIds[0]);
				}
			} else {
				setMerchantIdError("No merchant IDs found in your Executable account. Click the button below to create one.");
				setMerchantIds([]);
			}
		} catch (err) {
			console.error("Error fetching merchant IDs:", err);
			if (err.status === 403) {
				setMerchantIdError("Authorization error: You don't have permission to access this team's Apple merchant IDs.");
			} else {
				setMerchantIdError(err.message || "Failed to fetch merchant IDs. Please try again.");
			}
			setMerchantIds([]);
		} finally {
			setFetchingMerchantIds(false);
		}
	};

	const createMerchantId = async () => {
		if (!selectedTeam) return;

		setCreatingMerchantId(true);
		setMerchantIdError(null);

		try {
			const response = await api("/app", {
				method: "POST",
				body: {
					action: "create_merchant_id",
					teamId: selectedTeam.id,
					merchantName: `${selectedTeam.name} Payment Processing`,
				},
			});

			if (response && response.merchantId) {
				// Add the newly created merchant ID to the list and select it
				const newMerchantId = response.merchantId;
				setMerchantIds([newMerchantId]);
				setSelectedMerchantId(newMerchantId);

				// Update the appleConfigData with the merchant ID information
				setAppleConfigData((prev) => ({
					...prev,
					merchantId: newMerchantId.id,
					merchantIdentifier: newMerchantId.identifier,
					merchantName: newMerchantId.name,
				}));
			} else {
				setMerchantIdError("Failed to create a merchant ID. Please try again or create one manually in the Apple Developer Portal.");
			}
		} catch (err) {
			console.error("Error creating merchant ID:", err);
			if (err.status === 403) {
				setMerchantIdError("Authorization error: You don't have permission to create a merchant ID for this team.");
			} else {
				setMerchantIdError(err.message || "Failed to create merchant ID. Please try again.");
			}
		} finally {
			setCreatingMerchantId(false);
		}
	};

	const handleMerchantIdChange = (selectedId) => {
		const merchant = merchantIds.find((m) => m.id === selectedId);
		setSelectedMerchantId(merchant);

		// Update the appleConfigData with the merchant ID information
		if (merchant) {
			setAppleConfigData((prev) => ({
				...prev,
				merchantId: merchant.id,
				merchantIdentifier: merchant.identifier,
				merchantName: merchant.name,
			}));
		}
	};

	const spin = spinValue.interpolate({
		inputRange: [0, 1],
		outputRange: ["0deg", "360deg"],
	});

	const handleFileUpload = async (event) => {
		const file = event.target.files[0];
		if (file) {
			try {
				const reader = new FileReader();
				reader.onload = (e) => {
					setAppleConfigData({
						...appleConfigData,
						p8Key: e.target.result,
						p8FileName: file.name,
					});
				};
				reader.readAsText(file);
			} catch (err) {
				setError("Failed to read p8 key file");
			}
		}
	};

	const handleKeyIdChange = (text) => {
		// Reset p8Key if keyId changes
		if (text !== appleConfigData.keyId) {
			setAppleConfigData({
				keyId: text,
				p8Key: "",
				p8FileName: "",
			});
		} else {
			setAppleConfigData({
				...appleConfigData,
				keyId: text,
			});
		}
	};

	const handleAddToApp = async () => {
		try {
			setConfigSaving(true);
			setError(null);

			// Update the app to point to the team with the push notification keys
			await api("/app", {
				method: "POST",
				body: {
					action: "update_app",
					appId: appId,
					updates: {
						service: "payments",
						teamId: selectedTeam.id,
					},
				},
			});

			// Refresh the app config and teams
			await fetchConfig();
			await fetchTeams();
			onUpdate?.();
		} catch (err) {
			setError(err.message);
		} finally {
			setConfigSaving(false);
		}
	};

	const handleEditAppleConfig = () => {
		if (!appConfig.isAppleCoreActive) {
			setShowErrorDialog(true);
			return;
		}

		const teamApplePayConfig = selectedTeam?.services?.applePayConfig;

		// Otherwise, open the modal to configure or modify, pre-populating if config exists
		setAppleConfigData({
			keyId: teamApplePayConfig?.keyId || "",
			p8Key: "", // We don't get the actual key back for security
			p8FileName: teamApplePayConfig?.p8FileName || "", // Keep the existing file name
			// Pre-populate other fields from team config if available
			merchantId: teamApplePayConfig?.merchantId || null,
			merchantIdentifier: teamApplePayConfig?.merchantIdentifier || null,
			merchantName: teamApplePayConfig?.merchantName || null,
		});

		setShowAppleConfigModal(true);
	};

	const handleSaveAppleConfig = async () => {
		try {
			setConfigSaving(true);

			// Create an object with the Apple pay configuration
			const appleConfig = {
				merchantId: appleConfigData.merchantId,
				merchantIdentifier: appleConfigData.merchantIdentifier,
				merchantName: appleConfigData.merchantName,
			};

			// Only include stripeCertApplePay if a file was uploaded
			if (appleConfigData.stripeCertApplePay) {
				appleConfig.stripeCertApplePay = appleConfigData.stripeCertApplePay;
				appleConfig.stripeCertFileName = appleConfigData.stripeCertFileName;
			}

			// First, update the team with the Apple Pay configuration
			const teamResponse = await api("/team", {
				method: "PUT",
				body: {
					action: "ApplePay",
					teamId: selectedTeam.id,
					applePayConfig: appleConfig,
				},
			});

			// Then, update the app service to set the teamId, merchantId, and activate it
			if (teamResponse.teamId) {
				await api("/app", {
					method: "POST",
					body: {
						action: "update_app",
						appId: appId,
						updates: {
							service: "payments",
							teamId: teamResponse.teamId, // Link the service to the updated team
							merchantId: selectedMerchantId?.id, // Set the specific merchant ID for this app
							status: "needs_setup", // Activate the service for the app
						},
					},
				});
			}

			// Refresh the app config and teams
			// await fetchConfig(); // Removed refresh from here
			// await fetchTeams(); // Removed refresh from here
			setCurrentStep(6); // Go to step 6 on success
			setError(null);
			// onUpdate?.(); // Removed refresh from here
		} catch (err) {
			setError(err.message);
		} finally {
			setConfigSaving(false);
		}
	};

	// New function to handle close and refresh
	const handleCloseAndRefresh = async () => {
		try {
			// TODO: Optionally add a loading state here if refresh takes time
			await Promise.all([refetchData("app"), refetchData("team")]);
			onUpdate?.();
		} catch (err) {
			// Handle potential errors during refresh if necessary
			console.error("Error refreshing data on close:", err);
			setError("Failed to refresh settings. Please reload the page."); // Inform user
		} finally {
			setShowAppleConfigModal(false); // Close modal regardless of refresh success/failure
		}
	};

	// New function to handle activation and close
	const handleActivateServiceAndClose = async () => {
		setConfigSaving(true);
		setError(null);

		try {
			// Activate the service for the app
			await api("/app", {
				method: "POST",
				body: {
					action: "update_app",
					appId: appId,
					updates: {
						service: "payments",
						teamId: appleConfigData.teamId,
						merchantId: selectedMerchantId?.id,
						status: "active",
					},
				},
			});

			// On success, refresh and close
			await handleCloseAndRefresh();
		} catch (err) {
			console.error("Error activating payments service:", err);
			setError(`Failed to activate service: ${err.message}. Please try again or contact support.`);
		} finally {
			setConfigSaving(false);
		}
	};

	const handleNextStep = () => {
		setCurrentStep(currentStep + 1);
	};

	const handlePrevStep = () => {
		setCurrentStep(currentStep - 1);
	};

	const renderStepIndicator = () => {
		return (
			<View style={styles.stepIndicatorContainer}>
				{[1, 2, 3, 4, 5, 6].map((step) => (
					<View key={step} style={styles.stepIndicatorItem}>
						<View style={[styles.stepDot, currentStep >= step && styles.activeStepDot]} />
						<Text style={[styles.stepText, currentStep >= step && styles.activeStepText]}>Step {step}</Text>
					</View>
				))}
			</View>
		);
	};

	const renderStepOne = () => {
		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Introduction to Apple Pay Setup</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>In this process, you'll be setting up Apple Pay for your app by creating and configuring the following in the Apple Developer Portal:</Text>

					<View style={styles.setupItemContainer}>
						<View style={styles.setupItem}>
							<View style={styles.setupItemIconContainer}>
								<Ionicons name="card-outline" size={24} color="#0A84FF" />
							</View>
							<View style={styles.setupItemContent}>
								<Text style={styles.setupItemTitle}>Merchant ID</Text>
								<Text style={styles.setupItemDescription}>A unique identifier for your business that processes Apple Pay payments</Text>
							</View>
						</View>

						<View style={styles.setupItem}>
							<View style={styles.setupItemIconContainer}>
								<Ionicons name="shield-checkmark-outline" size={24} color="#0A84FF" />
							</View>
							<View style={styles.setupItemContent}>
								<Text style={styles.setupItemTitle}>Payment Processing Certificate</Text>
								<Text style={styles.setupItemDescription}>Encrypts payment data between Apple and your payment processor</Text>
							</View>
						</View>

						<View style={styles.setupItem}>
							<View style={styles.setupItemIconContainer}>
								<Ionicons name="finger-print-outline" size={24} color="#0A84FF" />
							</View>
							<View style={styles.setupItemContent}>
								<Text style={styles.setupItemTitle}>Merchant Identity Certificate</Text>
								<Text style={styles.setupItemDescription}>Authenticates your web sessions for Apple Pay transactions</Text>
							</View>
						</View>
					</View>

					<Text style={styles.stepNote}>Want to take a peek at the process before you start? Click the button below to open the Apple Developer Portal where you'll create your Merchant ID.</Text>

					<View style={styles.stepActionContainer}>
						<TouchableOpacity style={styles.stepActionButton} onPress={() => window.open("https://developer.apple.com/account/resources/identifiers/add/merchant", "_blank")}>
							<Text style={styles.stepActionButtonText}>Open Apple Developer Portal</Text>
							<Ionicons name="open-outline" size={16} color="#fff" />
						</TouchableOpacity>
					</View>
				</View>
			</View>
		);
	};

	const renderStepTwo = () => {
		// Get team name from selected team
		const teamName = selectedTeam?.name || "Your Team";
		const teamId = selectedTeam?.services?.appStore?.teamId || "XXXXXXXXXX";

		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Configure Merchant ID Settings</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>Now, you'll need to configure the Merchant ID in the Apple Developer Portal.</Text>

					<View style={styles.teamInfoContainer}>
						<Text style={styles.teamInfoLabel}>Team Information (imported from Apple Core Service)</Text>
						<View style={styles.teamInfoItem}>
							<Text style={styles.teamInfoTitle}>Team Name:</Text>
							<Text style={styles.teamInfoValue}>{teamName}</Text>
							<Ionicons name="lock-closed" size={16} color="#999" style={styles.teamInfoLock} />
						</View>
						<View style={styles.teamInfoItem}>
							<Text style={styles.teamInfoTitle}>Team ID:</Text>
							<Text style={styles.teamInfoValue}>{teamId}</Text>
							<Ionicons name="lock-closed" size={16} color="#999" style={styles.teamInfoLock} />
						</View>
					</View>

					<Text style={styles.stepNote}>This Apple Pay configuration will be linked to the team shown above. If you want to use a different Apple Developer account, please select a different team in the Apple Developer Core Service first.</Text>
				</View>
			</View>
		);
	};

	const renderStepThree = () => {
		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Select Merchant ID</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>Select an Apple Pay Merchant ID to use with this app. You'll need to create a Merchant ID in your Executable account first if you haven't done so already.</Text>

					{merchantIdError && (
						<View style={styles.errorContainer}>
							<Text style={styles.errorText}>{merchantIdError}</Text>
						</View>
					)}

					{fetchingMerchantIds ? (
						<View style={styles.loadingContainer}>
							<Animated.View style={{ transform: [{ rotate: spin }] }}>
								<Ionicons name="refresh" size={24} color="#fff" />
							</Animated.View>
							<Text style={styles.loadingText}>Retrieving merchant IDs from your account...</Text>
						</View>
					) : creatingMerchantId ? (
						<View style={styles.loadingContainer}>
							<Animated.View style={{ transform: [{ rotate: spin }] }}>
								<Ionicons name="refresh" size={24} color="#fff" />
							</Animated.View>
							<Text style={styles.loadingText}>Creating a new Merchant ID...</Text>
						</View>
					) : merchantIds.length > 0 ? (
						<View style={styles.merchantIdContainer}>
							<Text style={styles.merchantIdLabel}>Your Merchant IDs:</Text>

							<View style={styles.merchantIdPickerContainer}>
								<View style={styles.merchantIdPicker}>
									{merchantIds.map((merchant) => (
										<TouchableOpacity key={merchant.id} style={[styles.merchantIdOption, selectedMerchantId?.id === merchant.id && styles.merchantIdOptionSelected]} onPress={() => handleMerchantIdChange(merchant.id)}>
											<View style={styles.merchantIdOptionContent}>
												<Text style={[styles.merchantIdOptionText, selectedMerchantId?.id === merchant.id && styles.merchantIdOptionTextSelected]}>{merchant.name}</Text>
												<Text style={styles.merchantIdIdentifier}>{merchant.identifier}</Text>
											</View>
											<View style={styles.merchantIdStatusContainer}>
												<View style={[styles.statusDot, { backgroundColor: merchant.status === "ACTIVE" ? "#82C91E" : "#FFC107" }]} />
												<Text style={styles.merchantIdStatus}>{merchant.status}</Text>
												{selectedMerchantId?.id === merchant.id && <Ionicons name="checkmark-circle" size={20} color="#0A84FF" style={styles.merchantIdCheckmark} />}
											</View>
										</TouchableOpacity>
									))}
								</View>
							</View>

							{selectedMerchantId && (
								<View style={styles.selectedMerchantDetails}>
									<Text style={styles.selectedMerchantTitle}>Selected Merchant ID Details:</Text>
									<View style={styles.selectedMerchantItem}>
										<Text style={styles.selectedMerchantLabel}>Name:</Text>
										<Text style={styles.selectedMerchantValue}>{selectedMerchantId.name}</Text>
									</View>
									<View style={styles.selectedMerchantItem}>
										<Text style={styles.selectedMerchantLabel}>Identifier:</Text>
										<Text style={styles.selectedMerchantValue}>{selectedMerchantId.identifier}</Text>
									</View>
									<View style={styles.selectedMerchantItem}>
										<Text style={styles.selectedMerchantLabel}>Status:</Text>
										<Text style={styles.selectedMerchantValue}>
											<View style={[styles.statusDot, { backgroundColor: selectedMerchantId.status === "ACTIVE" ? "#82C91E" : "#FFC107", marginRight: 10 }]} />
											{selectedMerchantId.status}
										</Text>
									</View>
									{selectedMerchantId.createdAt && (
										<View style={styles.selectedMerchantItem}>
											<Text style={styles.selectedMerchantLabel}>Created:</Text>
											<Text style={styles.selectedMerchantValue}>{new Date(selectedMerchantId.createdAt).toLocaleString()}</Text>
										</View>
									)}
								</View>
							)}

							<TouchableOpacity style={styles.refreshButton} onPress={fetchMerchantIds}>
								<Ionicons name="refresh" size={16} color="#fff" />
								<Text style={styles.refreshButtonText}>Refresh Merchant IDs</Text>
							</TouchableOpacity>
						</View>
					) : (
						<View style={styles.emptyMerchantContainer}>
							<Ionicons name="card-outline" size={48} color="#666" style={styles.emptyMerchantIcon} />
							<Text style={styles.emptyMerchantTitle}>No Merchant IDs Found</Text>
							<Text style={styles.emptyMerchantText}>You don't have any Apple Pay Merchant IDs in your Executable account yet. Click the button below to create one.</Text>

							<View style={styles.stepActionContainer}>
								<TouchableOpacity style={styles.stepActionButton} onPress={createMerchantId} disabled={creatingMerchantId}>
									<Text style={styles.stepActionButtonText}>Create Merchant ID</Text>
									<Ionicons name="add-circle-outline" size={16} color="#fff" />
								</TouchableOpacity>
							</View>

							<Text style={styles.orText}>OR</Text>

							<Text style={styles.emptyMerchantText}>Create one manually in the Apple Developer Portal first, then create it here:</Text>

							<View style={styles.stepActionContainer}>
								<TouchableOpacity style={[styles.stepActionButton, styles.secondaryActionButton]} onPress={() => window.open("https://developer.apple.com/account/resources/identifiers/add/merchant", "_blank")}>
									<Text style={styles.stepActionButtonText}>Open Apple Portal</Text>
									<Ionicons name="open-outline" size={16} color="#fff" />
								</TouchableOpacity>

								<TouchableOpacity style={[styles.refreshButton, { marginLeft: 12 }]} onPress={fetchMerchantIds}>
									<Ionicons name="refresh" size={16} color="#fff" />
									<Text style={styles.refreshButtonText}>Refresh</Text>
								</TouchableOpacity>
							</View>
						</View>
					)}
				</View>
			</View>
		);
	};

	const renderStepFour = () => {
		// Get team info from selected team
		const teamName = selectedTeam?.name || "Your Team";
		const teamId = selectedTeam?.services?.appStore?.teamId || "XXXXXXXXXX";

		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Configure with Stripe</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>Now that you've created your merchant ID, you'll need to create a certificate in Stripe and upload it here.</Text>

					<View style={styles.teamInfoContainer}>
						<Text style={styles.teamInfoLabel}>Team Information (imported from Apple Core Service)</Text>
						<View style={styles.teamInfoItem}>
							<Text style={styles.teamInfoTitle}>Team Name:</Text>
							<Text style={styles.teamInfoValue}>{teamName}</Text>
							<Ionicons name="lock-closed" size={16} color="#999" style={styles.teamInfoLock} />
						</View>
						<View style={styles.teamInfoItem}>
							<Text style={styles.teamInfoTitle}>Team ID:</Text>
							<Text style={styles.teamInfoValue}>{teamId}</Text>
							<Ionicons name="lock-closed" size={16} color="#999" style={styles.teamInfoLock} />
						</View>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>1</Text>
						<Text style={styles.configInstructionText}>
							Visit the <Text style={styles.highlightText}>Stripe iOS Certificates Dashboard</Text> to create your Apple Pay certificate
						</Text>
					</View>

					<View style={styles.stepActionContainer}>
						<TouchableOpacity style={styles.stepActionButton} onPress={() => window.open("https://dashboard.stripe.com/settings/ios_certificates", "_blank")}>
							<Text style={styles.stepActionButtonText}>Open Stripe Dashboard</Text>
							<Ionicons name="open-outline" size={16} color="#fff" />
						</TouchableOpacity>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>2</Text>
						<Text style={styles.configInstructionText}>Follow Stripe's instructions to create a certificate and download the .cer file</Text>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>3</Text>
						<Text style={styles.configInstructionText}>Upload the .cer file below</Text>
					</View>

					<View style={styles.formGroup}>
						<Text style={styles.formLabel}>Apple Pay Certificate (.cer)</Text>
						<View style={styles.fileUploadContainer}>
							<Text style={styles.fileNameText}>{appleConfigData.stripeCertFileName || "No file selected"}</Text>
							<TouchableOpacity style={styles.fileUploadButton} onPress={() => document.getElementById("certFileUpload").click()}>
								<Text style={styles.fileUploadButtonText}>Choose File</Text>
							</TouchableOpacity>
							<input
								id="certFileUpload"
								type="file"
								accept=".cer"
								onChange={(e) => {
									const file = e.target.files[0];
									if (file) {
										try {
											const reader = new FileReader();
											reader.onload = (e) => {
												setAppleConfigData({
													...appleConfigData,
													stripeCertApplePay: e.target.result,
													stripeCertFileName: file.name,
												});
											};
											reader.readAsDataURL(file);
										} catch (err) {
											setError("Failed to read certificate file");
										}
									}
								}}
								style={{ display: "none" }}
							/>
						</View>
						<Text style={styles.formHelp}>Upload the .cer file you downloaded from Stripe</Text>
					</View>

					{error && (
						<View style={styles.errorContainer}>
							<Text style={styles.errorText}>{error}</Text>
						</View>
					)}
				</View>
			</View>
		);
	};

	const renderStepFive = () => {
		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Review Configuration</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>Review your Apple Pay configuration before saving. You'll need to complete the final step in the Apple Developer Portal after saving.</Text>

					<View style={styles.configSummaryContainer}>
						<Text style={styles.configSummaryTitle}>Configuration Summary</Text>

						<View style={styles.configSummaryItem}>
							<Text style={styles.configSummaryLabel}>Merchant ID:</Text>
							<Text style={styles.configSummaryValue}>{selectedMerchantId?.name || "Not selected"}</Text>
						</View>

						<View style={styles.configSummaryItem}>
							<Text style={styles.configSummaryLabel}>Merchant Identifier:</Text>
							<Text style={styles.configSummaryValue}>{selectedMerchantId?.identifier || "Not available"}</Text>
						</View>

						<View style={styles.configSummaryItem}>
							<Text style={styles.configSummaryLabel}>Certificate:</Text>
							<Text style={styles.configSummaryValue}>{appleConfigData.stripeCertFileName || "Not uploaded"}</Text>
						</View>
					</View>

					<View style={styles.infoBox}>
						<Text style={styles.infoBoxText}>After saving this configuration, you'll need to complete one final step in the Apple Developer Portal to associate your merchant ID with your app.</Text>
					</View>
				</View>
			</View>
		);
	};

	const renderStepSix = () => {
		const bundleId = app?.services?.find((service) => service.type === "apple_developer")?.config?.bundleId || "com.example.yourapp";
		const appleBundleId = app?.services?.find((service) => service.type === "apple_developer")?.config?.appleBundleId || "ABC123DEF";
		const merchantIdentifier = selectedMerchantId?.identifier || "merchant.com.example.yourapp";
		const editUrl = `https://developer.apple.com/account/resources/identifiers/bundleId/edit/${appleBundleId}`;

		console.log("bundleId", bundleId);
		console.log("appleBundleId", appleBundleId);
		console.log("merchantIdentifier", merchantIdentifier);
		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Final Configuration: Apple Developer Portal</Text>
				<View style={styles.stepContentBox}>
					<View style={styles.successContainer}>
						<Ionicons name="checkmark-circle" size={48} color="#82C91E" style={styles.successIcon} />
						<Text style={styles.successTitle}>Configuration Ready</Text>
						<Text style={styles.successText}>Your Executable configuration is complete. One final step is required on the Apple Developer website.</Text>
					</View>

					<Text style={styles.stepInstructions}>
						You need to associate the Merchant ID (<Text style={styles.codeText}>{merchantIdentifier}</Text>) with your App ID (<Text style={styles.codeText}>{bundleId}</Text>) in the Apple Developer Portal.
					</Text>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>1</Text>
						<Text style={styles.configInstructionText}>
							Go to the <Text style={styles.highlightText}>Identifiers</Text> section of your Apple Developer account, or click the button below to go directly to your App ID's configuration.
						</Text>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>2</Text>
						<Text style={styles.configInstructionText}>
							Scroll down to the <Text style={styles.highlightText}>Apple Pay Payment Processing</Text> capability and click <Text style={styles.highlightText}>Configure</Text>.
						</Text>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>3</Text>
						<Text style={styles.configInstructionText}>
							Select the Merchant ID (<Text style={styles.codeText}>{merchantIdentifier}</Text>) you configured earlier and click <Text style={styles.highlightText}>Continue</Text>, then <Text style={styles.highlightText}>Save</Text>.
						</Text>
					</View>

					<View style={styles.stepActionContainer}>
						<TouchableOpacity style={styles.stepActionButton} onPress={() => window.open(editUrl, "_blank")}>
							<Text style={styles.stepActionButtonText}>Open App ID Configuration</Text>
							<Ionicons name="open-outline" size={16} color="#fff" />
						</TouchableOpacity>
					</View>

					<Text style={styles.stepNote}>After completing these steps, click "Finish Setup" below to save your configuration!</Text>
				</View>
			</View>
		);
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return renderStepOne();
			case 2:
				return renderStepTwo();
			case 3:
				return renderStepThree();
			case 4:
				return renderStepFour();
			case 5:
				return renderStepFive();
			case 6:
				return renderStepSix();
			default:
				return null;
		}
	};

	const renderStepActions = () => {
		const isStepThreeDisabled = currentStep === 3 && (fetchingMerchantIds || creatingMerchantId || (!selectedMerchantId && merchantIds.length > 0));
		const isStepFourDisabled = currentStep === 4 && !appleConfigData.stripeCertFileName;
		const isStepFiveDisabled = currentStep === 5 && (!appleConfigData.stripeCertApplePay || !appleConfigData.stripeCertFileName);
		const isStepSixDisabled = false;

		return (
			<View style={styles.stepActionsContainer}>
				{
					<TouchableOpacity
						style={[styles.backButton, { opacity: currentStep > 1 && !fetchingMerchantIds && !creatingMerchantId ? 1 : 0.5 }]}
						onPress={handlePrevStep}
						disabled={currentStep <= 1 || fetchingMerchantIds || creatingMerchantId || configSaving}>
						<Ionicons name="arrow-back" size={16} color="#fff" />
						<Text style={styles.backButtonText}>Back</Text>
					</TouchableOpacity>
				}

				<View style={styles.actionsRight}>
					{currentStep < 5 ? (
						<TouchableOpacity
							style={[styles.nextButton, ((currentStep === 3 && isStepThreeDisabled) || (currentStep === 4 && isStepFourDisabled)) && styles.nextButtonDisabled]}
							onPress={handleNextStep}
							disabled={(currentStep === 3 && isStepThreeDisabled) || (currentStep === 4 && isStepFourDisabled)}>
							<Text style={styles.nextButtonText}>Next</Text>
							<Ionicons name="arrow-forward" size={16} color="#fff" />
						</TouchableOpacity>
					) : currentStep === 5 ? (
						<TouchableOpacity style={[styles.saveButton, (isStepFiveDisabled || configSaving) && styles.saveButtonDisabled]} onPress={handleSaveAppleConfig} disabled={isStepFiveDisabled || configSaving}>
							{configSaving ? (
								<View style={styles.buttonContent}>
									<Animated.View style={{ transform: [{ rotate: spin }] }}>
										<Ionicons name="refresh" size={16} color="#fff" />
									</Animated.View>
									<Text style={styles.saveButtonText}>Saving...</Text>
								</View>
							) : (
								<Text style={styles.saveButtonText}>Save and Continue</Text>
							)}
						</TouchableOpacity>
					) : currentStep === 6 ? (
						<TouchableOpacity style={[styles.saveButton, configSaving && styles.saveButtonDisabled]} onPress={handleActivateServiceAndClose} disabled={configSaving}>
							{configSaving ? (
								<View style={styles.buttonContent}>
									<Animated.View style={{ transform: [{ rotate: spin }] }}>
										<Ionicons name="refresh" size={16} color="#fff" />
									</Animated.View>
									<Text style={styles.saveButtonText}>Activating...</Text>
								</View>
							) : (
								<Text style={styles.saveButtonText}>Finish Setup</Text>
							)}
						</TouchableOpacity>
					) : null}
				</View>
			</View>
		);
	};

	const renderAppleConfigModal = () => {
		return (
			<View style={styles.modalOverlay}>
				<View style={styles.modal}>
					<View style={styles.modalHeader}>
						<Text style={styles.modalTitle}>Configure Apple Pay</Text>
						<TouchableOpacity onPress={() => setShowAppleConfigModal(false)}>
							<Ionicons name="close" size={24} color="#fff" />
						</TouchableOpacity>
					</View>

					{renderStepIndicator()}

					<View style={styles.modalContent}>{renderStepContent()}</View>

					<View style={styles.modalFooter}>{renderStepActions()}</View>
				</View>
			</View>
		);
	};

	const renderErrorDialog = () => {
		return (
			<View style={styles.modalOverlay}>
				<View style={[styles.modal, { maxWidth: 450 }]}>
					<View style={styles.modalHeader}>
						<Text style={styles.modalTitle}>Dependency Not Met</Text>
						<TouchableOpacity onPress={() => setShowErrorDialog(false)}>
							<Ionicons name="close" size={24} color="#fff" />
						</TouchableOpacity>
					</View>
					<View style={styles.modalContent}>
						<View style={styles.errorDialogContent}>
							<Ionicons name="alert-circle" size={48} color="#FF453A" style={styles.errorIcon} />
							<Text style={styles.errorDialogTitle}>Apple Core Service Not Active</Text>
							<Text style={styles.errorDialogText}>Please ensure the Apple Developer Core Service is in active state before configuring push notifications.</Text>
						</View>
						<View style={[styles.modalActions, { justifyContent: "center" }]}>
							<TouchableOpacity style={styles.createButton} onPress={() => setShowErrorDialog(false)}>
								<Text style={styles.createButtonText}>Understood!</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</View>
		);
	};

	const renderAppleConfigSection = () => {
		const isAppleCoreActive = appConfig?.isAppleCoreActive;
		const teamApplePayConfig = selectedTeam?.services?.applePay;
		const hasTeamConfig = teamApplePayConfig && teamApplePayConfig.merchantId && teamApplePayConfig.publishableKeyLive;
		const isServiceActive = appConfig?.serviceStatus === "active";
		// Determine button state
		let buttonState = "locked";
		let buttonText = "Locked";
		let buttonIcon = "lock-closed";
		let buttonColor = "#FF453A";

		if (isAppleCoreActive) {
			if (hasTeamConfig && isServiceActive) {
				buttonState = "edit";
				buttonText = "Modify";
				buttonIcon = "pencil";
				buttonColor = "#0A84FF";
			} else {
				buttonState = "configure";
				buttonText = "Configure";
				buttonIcon = "add";
				buttonColor = "#0A84FF";
			}
		}

		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>Apple Pay</Text>
					<TouchableOpacity style={[styles.configButton, buttonState === "locked" ? styles.lockedButton : buttonState === "edit" ? styles.editButton : styles.addButton]} onPress={handleEditAppleConfig} disabled={configSaving}>
						{configSaving && buttonState === "add" ? (
							<View style={styles.buttonContent}>
								<Animated.View style={{ transform: [{ rotate: spin }] }}>
									<Ionicons name="refresh" size={16} color={buttonColor} />
								</Animated.View>
								<Text style={[styles.configButtonText, { color: buttonColor }]}>{buttonText}</Text>
							</View>
						) : (
							<>
								<Ionicons name={buttonIcon} size={16} color={buttonColor} />
								<Text style={[styles.configButtonText, { color: buttonColor }]}>{buttonText}</Text>
							</>
						)}
					</TouchableOpacity>
				</View>

				{isServiceActive ? (
					<View style={styles.configDetails}>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Team</Text>
							<Text style={styles.configValue}>{selectedTeam?.name || "Unknown Team"}</Text>
						</View>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Merchant ID</Text>
							<Text style={styles.configValue}>{teamApplePayConfig?.merchantId || "Not configured"}</Text>
						</View>
						<View style={styles.configStatus}>
							<View style={[styles.statusDot, appConfig.serviceStatus === "active" ? styles.activeStatusDot : { backgroundColor: "#FFC107" }]} />
							<Text style={[styles.statusText, appConfig.serviceStatus !== "active" && { color: "#FFC107" }]}>{appConfig.serviceStatus === "active" ? "Active" : appConfig.serviceStatus}</Text>
						</View>
					</View>
				) : (
					<View style={styles.emptyConfig}>
						<Text style={styles.emptyConfigText}>Apple Pay not configured</Text>
						<Text style={styles.emptyConfigSubtext}>
							{isAppleCoreActive
								? "Configure your apps and websites to process Apple Pay transactions for physical goods and services. You'll need to upload your Apple Merchant ID Authentication Key (.p8) from the Apple Developer Portal."
								: "Apple Developer Core Service must be active before configuring Apple Pay."}
						</Text>
					</View>
				)}
			</View>
		);
	};

	const renderGoogleConfigSection = () => {
		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>Google Push Notifications</Text>
					<View style={styles.disabledBadge}>
						<Text style={styles.disabledBadgeText}>Coming Soon</Text>
					</View>
				</View>
				<View style={styles.emptyConfig}>
					<Text style={styles.emptyConfigText}>Google Push Notifications not available yet</Text>
					<Text style={styles.emptyConfigSubtext}>Support for Google Push Notifications is coming soon. This will allow you to send notifications to Android devices.</Text>
				</View>
			</View>
		);
	};

	// Function to get Stripe Connect onboarding URL
	const getStripeConnectURL = async () => {
		setFetchingConnectURL(true);
		setStripeConnectError(null);

		try {
			const response = await api("/app", {
				method: "POST",
				body: {
					action: "get_stripe_connect_url",
					appId: appId,
					teamId: selectedTeam?.id,
					tenantId: appConfig?.tenantId,
					returnUrl: window.location.href, // The URL to return to after onboarding
				},
			});

			if (response && response.url) {
				// Redirect the user to the Stripe Connect onboarding URL
				window.location.href = response.url;
			} else {
				setStripeConnectError("Failed to get Stripe Connect URL. Please try again.");
			}
		} catch (err) {
			console.error("Error getting Stripe Connect URL:", err);
			setStripeConnectError(err.message || "Failed to get Stripe Connect URL. Please try again.");
		} finally {
			setFetchingConnectURL(false);
		}
	};

	// Function to disconnect Stripe Connect account
	const disconnectStripeConnect = async () => {
		setConfigSaving(true);
		setStripeConnectError(null);

		try {
			await api("/app", {
				method: "POST",
				body: {
					action: "disconnect_stripe_connect",
					appId: appId,
					teamId: selectedTeam?.id,
				},
			});

			// Update the local state to reflect disconnection
			setStripeConnectData({
				accountId: "",
				status: "not_connected",
				lastUpdated: new Date().toISOString(),
			});

			// Refresh the app data
			await refetchData("app");
			onUpdate?.();
		} catch (err) {
			console.error("Error disconnecting Stripe Connect:", err);
			setStripeConnectError(err.message || "Failed to disconnect Stripe Connect. Please try again.");
		} finally {
			setConfigSaving(false);
		}
	};

	// New function to render the Stripe Connect section
	const renderStripeConnectSection = () => {
		// Determine state based on stripeConnectData
		const hasAccountId = !!stripeConnectData.accountId;
		const status = stripeConnectData.status;
		const isReady = status === "connected" || status === "active";
		const isPending = status === "pending";
		const isError = status === "error" || !appConfig?.tenantId; // Assuming 'error' is a possible status
		const isNotConnected = !hasAccountId || status === "not_connected";

		// Determine status display
		let statusColor = "#FFC107"; // Default yellow
		let statusIcon = "help-circle-outline"; // Default icon
		let statusText = "Unknown";

		if (isReady) {
			statusColor = "#82C91E"; // Green
			statusIcon = "checkmark-circle";
			statusText = "Connected";
		} else if (isPending) {
			statusColor = "#FFC107"; // Yellow
			statusIcon = "hourglass-outline";
			statusText = "Pending";
		} else if (isError) {
			statusColor = "#FF453A"; // Red
			statusIcon = "alert-circle";
			statusText = "Error";
		} else if (isNotConnected) {
			statusColor = "#666"; // Grey for not connected
			statusIcon = "close-circle-outline";
			statusText = "Not Connected";
		} else if (appConfig.tenantId) {
			statusColor = "#FFC107";
			statusIcon = "hourglass-outline";
			statusText = "No Accounts Service";
		}
		// Keep original defaults if status is unexpected
		else {
			statusColor = "#FFC107";
			statusIcon = "time-outline";
			statusText = status || "Not Connected"; // Display actual status if unknown but present
		}

		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>Stripe Connect</Text>
					{/* Conditional Buttons */}
					{
						isReady ? ( // Show disconnect only when fully connected/active
							<TouchableOpacity style={[styles.configButton, styles.disconnectButton]} onPress={disconnectStripeConnect} disabled={configSaving || fetchingConnectURL}>
								{configSaving ? (
									<View style={styles.buttonContent}>
										<Animated.View style={{ transform: [{ rotate: spin }] }}>
											<Ionicons name="refresh" size={16} color="#FF453A" />
										</Animated.View>
										<Text style={[styles.configButtonText, { color: "#FF453A" }]}>Disconnecting...</Text>
									</View>
								) : (
									<>
										<Ionicons name="close-circle" size={16} color="#FF453A" />
										<Text style={[styles.configButtonText, { color: "#FF453A" }]}>Disconnect</Text>
									</>
								)}
							</TouchableOpacity>
						) : isPending ? ( // Show "Retry Connection" button when in pending state
							<TouchableOpacity style={[styles.configButton, { backgroundColor: "#FFC10715" }]} onPress={disconnectStripeConnect} disabled={configSaving || fetchingConnectURL}>
								{configSaving ? (
									<View style={styles.buttonContent}>
										<Animated.View style={{ transform: [{ rotate: spin }] }}>
											<Ionicons name="refresh" size={16} color="#FFC107" />
										</Animated.View>
										<Text style={[styles.configButtonText, { color: "#FFC107" }]}>Retrying...</Text>
									</View>
								) : (
									<>
										<Ionicons name="refresh-circle" size={16} color="#FFC107" />
										<Text style={[styles.configButtonText, { color: "#FFC107" }]}>Retry Connection</Text>
									</>
								)}
							</TouchableOpacity>
						) : isNotConnected ? ( // Show connect only when not connected and no accountId exists
							<TouchableOpacity style={[styles.configButton, styles.connectButton]} onPress={getStripeConnectURL} disabled={configSaving || fetchingConnectURL}>
								{fetchingConnectURL ? (
									<View style={styles.buttonContent}>
										<Animated.View style={{ transform: [{ rotate: spin }] }}>
											<Ionicons name="refresh" size={16} color="#0A84FF" />
										</Animated.View>
										<Text style={[styles.configButtonText, { color: "#0A84FF" }]}>Connecting...</Text>
									</View>
								) : (
									<>
										<Ionicons name="link" size={16} color="#0A84FF" />
										<Text style={[styles.configButtonText, { color: "#0A84FF" }]}>Connect</Text>
									</>
								)}
							</TouchableOpacity>
						) : null /* No button for Error states */
					}
				</View>

				{stripeConnectError && (
					<View style={styles.errorContainer}>
						<Text style={styles.errorText}>{stripeConnectError}</Text>
					</View>
				)}

				{successMessage && (
					<View style={styles.successContainer}>
						<Text style={styles.successText}>{successMessage}</Text>
					</View>
				)}

				{/* Add Publishable Keys at the top of the config details section */}
				<View style={styles.configDetails}>
					{/* Only show publishable keys if Stripe Connect is properly connected */}
					{hasAccountId && isReady && (
						<>
							{/* Add Stripe Publishable Keys */}
							{renderPublishableKeyField(
								"Test Publishable Key",
								stripeConnectData.publishableKeyTest || "",
								"pk_test_...",
								editingTestKey,
								setEditingTestKey,
								(text) => setStripeConnectData({ ...stripeConnectData, publishableKeyTest: text }),
								"https://dashboard.stripe.com/test/apikeys"
							)}

							{renderPublishableKeyField(
								"Live Publishable Key",
								stripeConnectData.publishableKeyLive || "",
								"pk_live_...",
								editingLiveKey,
								setEditingLiveKey,
								(text) => setStripeConnectData({ ...stripeConnectData, publishableKeyLive: text }),
								"https://dashboard.stripe.com/apikeys"
							)}

							{/* Show a Save button if either key is being edited */}
							{(editingTestKey || editingLiveKey) && (
								<TouchableOpacity style={styles.savePublishableKeysButton} onPress={savePublishableKeys} disabled={configSaving}>
									{configSaving ? (
										<View style={styles.buttonContent}>
											<Animated.View style={{ transform: [{ rotate: spin }] }}>
												<Ionicons name="refresh" size={16} color="#fff" />
											</Animated.View>
											<Text style={styles.savePublishableKeysButtonText}>Saving...</Text>
										</View>
									) : (
										<Text style={styles.savePublishableKeysButtonText}>Save Keys</Text>
									)}
								</TouchableOpacity>
							)}

							{/* Divider between keys and account info */}
							<View style={styles.keysDivider} />
						</>
					)}

					{/* Show details if accountId exists, regardless of status */}
					{hasAccountId && (
						<>
							<View style={styles.configItem}>
								<Text style={styles.configLabel}>Display Name</Text>
								<Text style={styles.configValue}>{stripeConnectData.dashboard_display_name}</Text>
							</View>
							<View style={styles.configItem}>
								<Text style={styles.configLabel}>Account ID</Text>
								<Text style={styles.configValue}>{stripeConnectData.accountId}</Text>
							</View>
							{stripeConnectData.lastUpdated && (
								<View style={styles.configItem}>
									<Text style={styles.configLabel}>Last Updated</Text>
									<Text style={styles.configValue}>{new Date(stripeConnectData.lastUpdated).toLocaleString()}</Text>
								</View>
							)}
							<View style={styles.configStatus}>
								<Ionicons name={statusIcon} size={16} color={statusColor} style={{ marginRight: 8 }} />
								<Text style={[styles.statusText, { color: statusColor }]}>{statusText}</Text>
							</View>
							{/* Add message for pending/error states */}
							{isPending && <Text style={[styles.emptyConfigSubtext, { marginTop: 10, textAlign: "left" }]}>Your Stripe account connection is pending verification. Please check Stripe for updates.</Text>}
							{isError && <Text style={[styles.errorText, { marginTop: 10, textAlign: "left" }]}>There was an error with your Stripe connection. Please check Stripe or try reconnecting if possible.</Text>}
							{!appConfig.tenantId && <Text style={[styles.errorText, { marginTop: 10, textAlign: "left" }]}>Please add an Accounts Service to your app to connect your Stripe account.</Text>}
						</>
					)}
				</View>

				{/* Show connect info only when truly not connected */}
				{!hasAccountId && (
					<View style={styles.emptyConfig}>
						<Ionicons name="card-outline" size={40} color="#666" style={{ marginBottom: 16 }} />
						<Text style={styles.emptyConfigText}>Stripe Connect not configured</Text>
						<Text style={styles.emptyConfigSubtext}>Connect your Stripe account to process payments on behalf of your users. This allows you to create a marketplace or platform where you can take a fee from each transaction.</Text>

						<View style={styles.connectInfoContainer}>
							<View style={styles.connectInfoItem}>
								<Ionicons name="wallet-outline" size={24} color="#0A84FF" style={styles.connectInfoIcon} />
								<View style={styles.connectInfoContent}>
									<Text style={styles.connectInfoTitle}>Process payments on behalf of your users</Text>
									<Text style={styles.connectInfoDescription}>Accept payments and pay out to connected accounts</Text>
								</View>
							</View>

							<View style={styles.connectInfoItem}>
								<Ionicons name="cash-outline" size={24} color="#0A84FF" style={styles.connectInfoIcon} />
								<View style={styles.connectInfoContent}>
									<Text style={styles.connectInfoTitle}>Take platform fees</Text>
									<Text style={styles.connectInfoDescription}>Automatically collect your commission from each transaction</Text>
								</View>
							</View>

							<View style={styles.connectInfoItem}>
								<Ionicons name="shield-checkmark-outline" size={24} color="#0A84FF" style={styles.connectInfoIcon} />
								<View style={styles.connectInfoContent}>
									<Text style={styles.connectInfoTitle}>Compliant and secure</Text>
									<Text style={styles.connectInfoDescription}>Stripe handles verification, reporting, and regulatory requirements</Text>
								</View>
							</View>
						</View>
					</View>
				)}
			</View>
		);
	};

	// New function to render publishable key field with edit button
	const renderPublishableKeyField = (label, value, placeholder, isEditing, setEditing, onChange, dashboardLink) => {
		const maskKey = (key) => {
			if (!key) return "";
			return key.substring(0, 12) + "..." + key.substring(key.length - 4);
		};

		return (
			<View style={styles.stripePubKeyContainer}>
				<View style={styles.stripePubKeyHeader}>
					<Text style={styles.stripePubKeyLabel}>{label}</Text>
					<TouchableOpacity style={styles.editButton} onPress={() => setEditing(!isEditing)}>
						<Ionicons name={isEditing ? "close" : "pencil"} size={16} color="#0A84FF" />
						<Text style={styles.editButtonText}>{isEditing ? "Cancel" : "Edit"}</Text>
					</TouchableOpacity>
				</View>

				{isEditing ? (
					<View>
						<TextInput style={styles.stripePubKeyInput} value={value} onChangeText={onChange} placeholder={placeholder} placeholderTextColor="#666" />
						<Text style={styles.stripePubKeyHelp}>
							Get your {label.toLowerCase()} from the
							<TouchableOpacity onPress={() => window.open(dashboardLink, "_blank")}>
								<Text style={styles.linkText}> Stripe Dashboard</Text>
							</TouchableOpacity>
						</Text>
					</View>
				) : (
					<Text style={styles.stripePubKeyValue}>{value ? maskKey(value) : "Not configured"}</Text>
				)}
			</View>
		);
	};

	// Update the save function to handle saving keys to the team
	const savePublishableKeys = async () => {
		if (!selectedTeam) return;

		setConfigSaving(true);
		setError(null);

		try {
			// Call the API to save the publishable keys
			const response = await api("/app", {
				method: "POST",
				body: {
					action: "update_payment_keys",
					teamId: selectedTeam.id,
					publishableKeyTest: stripeConnectData.publishableKeyTest,
					publishableKeyLive: stripeConnectData.publishableKeyLive,
				},
			});

			if (response && response.success) {
				// Update was successful
				setEditingTestKey(false);
				setEditingLiveKey(false);

				// Optional: update the local state with the returned values
				setStripeConnectData((prev) => ({
					...prev,
					publishableKeyTest: response.publishableKeyTest || prev.publishableKeyTest,
					publishableKeyLive: response.publishableKeyLive || prev.publishableKeyLive,
				}));

				// Show success message
				setError(null);
				setSuccessMessage("Stripe publishable keys saved successfully");

				// Clear success message after 3 seconds
				setTimeout(() => {
					setSuccessMessage(null);
				}, 3000);
			} else {
				throw new Error("Failed to save keys. Please try again.");
			}
		} catch (err) {
			console.error("Error saving publishable keys:", err);
			setError("Failed to save publishable keys. Please try again.");
		} finally {
			setConfigSaving(false);
		}
	};

	if (loading.app || loading.team) {
		return (
			<View style={styles.container}>
				<View style={styles.loadingContainer}>
					<Animated.View style={{ transform: [{ rotate: spin }] }}>
						<Ionicons name="refresh" size={24} color="#fff" />
					</Animated.View>
					<Text style={styles.loadingText}>Loading payment settings...</Text>
				</View>
			</View>
		);
	}

	// Check if Stripe Connect is ready before showing Apple Pay
	const isStripeConnected = stripeConnectData.accountId && (stripeConnectData.status === "connected" || stripeConnectData.status === "active");

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<Text style={styles.title}>Payments</Text>
				<Text style={styles.subtitle}>Configure payments services for your app. This allows you to accept payments from your users using your Stripe account.</Text>
			</View>

			{error && (
				<View style={styles.errorContainer}>
					<Text style={styles.errorText}>{error}</Text>
				</View>
			)}

			{renderStripeConnectSection()}

			{isStripeConnected ? (
				// Only show Apple Pay section if Stripe Connect is ready
				renderAppleConfigSection()
			) : (
				// Show a message about needing to connect Stripe first
				<View style={styles.configSection}>
					<View style={styles.configHeader}>
						<Text style={styles.configTitle}>Apple Pay</Text>
						<View style={styles.disabledBadge}>
							<Text style={styles.disabledBadgeText}>Connect Stripe First</Text>
						</View>
					</View>
					<View style={styles.emptyConfig}>
						<Ionicons name="card-outline" size={40} color="#666" style={{ marginBottom: 16 }} />
						<Text style={styles.emptyConfigText}>Stripe Connection Required</Text>
						<Text style={styles.emptyConfigSubtext}>You need to connect your Stripe account before configuring Apple Pay. Please complete the Stripe Connect setup above first.</Text>
					</View>
				</View>
			)}
			{false && renderGoogleConfigSection()}

			{showAppleConfigModal && renderAppleConfigModal()}
			{showErrorDialog && renderErrorDialog()}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#333",
		borderRadius: 12,
		padding: 20,
	},
	header: {
		marginBottom: 24,
	},
	title: {
		fontSize: 24,
		fontWeight: "700",
		color: "#fff",
		marginBottom: 8,
	},
	subtitle: {
		fontSize: 14,
		color: "#999",
		lineHeight: 20,
	},
	loadingContainer: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
		gap: 16,
	},
	loadingText: {
		color: "#fff",
		fontSize: 16,
	},
	errorContainer: {
		backgroundColor: "#FF453A15",
		padding: 12,
		borderRadius: 8,
		marginBottom: 16,
	},
	errorText: {
		color: "#FF453A",
		fontSize: 14,
	},
	configSection: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 20,
		marginBottom: 24,
	},
	configHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 16,
	},
	configTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
	},
	configButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 8,
		borderRadius: 8,
	},
	addButton: {
		backgroundColor: "#0A84FF15",
	},
	lockedButton: {
		backgroundColor: "#FF453A15",
	},
	editButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 4,
		padding: 4,
		borderRadius: 4,
		backgroundColor: "#0A84FF15",
	},
	editButtonText: {
		fontSize: 12,
		fontWeight: "500",
		color: "#0A84FF",
	},
	configButtonText: {
		fontSize: 14,
		fontWeight: "500",
	},
	activeButtonText: {
		color: "#0A84FF",
	},
	lockedButtonText: {
		color: "#FF453A",
	},
	configDetails: {
		backgroundColor: "#33333350",
		borderRadius: 8,
		padding: 16,
	},
	configItem: {
		marginBottom: 12,
	},
	configLabel: {
		color: "#666",
		fontSize: 12,
		marginBottom: 4,
	},
	configValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
	},
	configStatus: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		marginTop: 16,
		paddingTop: 16,
		borderTopWidth: 1,
		borderTopColor: "#404040",
	},
	statusDot: {
		width: 8,
		height: 8,
		borderRadius: 4,
	},
	activeStatusDot: {
		backgroundColor: "#82C91E",
	},
	statusText: {
		color: "#82C91E",
		fontSize: 14,
		fontWeight: "500",
	},
	emptyConfig: {
		padding: 24,
		alignItems: "center",
		backgroundColor: "#33333350",
		borderRadius: 8,
	},
	emptyConfigText: {
		color: "#fff",
		fontSize: 15,
		fontWeight: "500",
		marginBottom: 8,
		textAlign: "center",
	},
	emptyConfigSubtext: {
		color: "#666",
		fontSize: 13,
		textAlign: "center",
		lineHeight: 18,
	},
	modalOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.3)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 3,
	},
	modal: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		width: "70%",
		maxWidth: 800,
		maxHeight: "90%",
		display: "flex",
		flexDirection: "column",
	},
	modalHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 20,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	modalTitle: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
	},
	modalContent: {
		padding: 24,
		flex: 1,
		overflow: "auto",
	},
	modalFooter: {
		borderTopWidth: 1,
		borderTopColor: "#333",
		padding: 16,
	},
	stepIndicatorContainer: {
		flexDirection: "row",
		backgroundColor: "#222",
		padding: 12,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	stepIndicatorItem: {
		flex: 1,
		alignItems: "center",
		position: "relative",
	},
	stepDot: {
		width: 12,
		height: 12,
		borderRadius: 6,
		backgroundColor: "#444",
		marginBottom: 4,
	},
	activeStepDot: {
		backgroundColor: "#0A84FF",
	},
	stepText: {
		fontSize: 12,
		color: "#666",
	},
	activeStepText: {
		color: "#fff",
		fontWeight: "500",
	},
	stepContainer: {
		flex: 1,
	},
	stepHeading: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 16,
	},
	stepContentBox: {
		backgroundColor: "#33333350",
		borderRadius: 8,
		padding: 20,
	},
	stepInstructions: {
		color: "#fff",
		fontSize: 15,
		marginBottom: 20,
		lineHeight: 22,
	},
	stepActionContainer: {
		marginBottom: 20,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		flexWrap: "wrap",
		gap: 12,
	},
	stepActionButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 8,
		padding: 12,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	},
	stepActionButtonDisabled: {
		backgroundColor: "#0A84FF50",
	},
	stepActionButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	stepNote: {
		color: "#999",
		fontSize: 13,
		fontStyle: "italic",
		lineHeight: 18,
	},
	stepNoteDanger: {
		color: "#FF453A",
		fontSize: 13,
		fontStyle: "italic",
		lineHeight: 18,
	},
	configInstructionItem: {
		flexDirection: "row",
		marginBottom: 16,
		alignItems: "flex-start",
	},
	configInstructionNumber: {
		width: 24,
		height: 24,
		borderRadius: 12,
		backgroundColor: "#0A84FF",
		textAlign: "center",
		lineHeight: 24,
		color: "#fff",
		fontWeight: "600",
		marginRight: 12,
	},
	configInstructionText: {
		flex: 1,
		color: "#fff",
		fontSize: 14,
		lineHeight: 20,
	},
	highlightText: {
		color: "#0A84FF",
		fontWeight: "600",
	},
	codeText: {
		fontFamily: "monospace",
		backgroundColor: "#222",
		color: "#fff",
		padding: 4,
		borderRadius: 4,
		marginLeft: 4,
	},
	teamInfoContainer: {
		backgroundColor: "#222",
		borderRadius: 8,
		padding: 16,
		marginBottom: 20,
	},
	teamInfoLabel: {
		color: "#999",
		fontSize: 12,
		marginBottom: 12,
	},
	teamInfoItem: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 8,
	},
	teamInfoTitle: {
		color: "#999",
		fontSize: 14,
		width: 100,
	},
	teamInfoValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
		flex: 1,
	},
	teamInfoLock: {
		marginLeft: 8,
	},
	formHelp: {
		color: "#999",
		fontSize: 12,
		marginTop: 4,
	},
	stepActionsContainer: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	actionsRight: {
		flexDirection: "row",
		gap: 12,
	},
	backButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#333",
	},
	backButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	nextButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#0A84FF",
	},
	nextButtonDisabled: {
		backgroundColor: "#0A84FF50",
	},
	nextButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	saveButton: {
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#0A84FF",
		minWidth: 160,
		alignItems: "center",
	},
	saveButtonDisabled: {
		backgroundColor: "#0A84FF50",
	},
	saveButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	buttonContent: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	},
	disabledBadge: {
		backgroundColor: "#333",
		borderRadius: 12,
		paddingVertical: 4,
		paddingHorizontal: 8,
	},
	disabledBadgeText: {
		color: "#999",
		fontSize: 12,
		fontWeight: "500",
	},
	errorDialogContent: {
		alignItems: "center",
		padding: 16,
	},
	errorIcon: {
		marginBottom: 16,
	},
	errorDialogTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 12,
		textAlign: "center",
	},
	errorDialogText: {
		fontSize: 14,
		color: "#999",
		textAlign: "center",
		lineHeight: 20,
	},
	formGroup: {
		marginBottom: 20,
	},
	formLabel: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
		marginBottom: 8,
	},
	formInput: {
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
		borderWidth: 1,
		borderColor: "#444",
	},
	fileUploadContainer: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		gap: 12,
		borderWidth: 1,
		borderColor: "#444",
	},
	fileNameText: {
		flex: 1,
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
	},
	fileUploadButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 6,
		paddingVertical: 8,
		paddingHorizontal: 12,
	},
	fileUploadButtonText: {
		color: "#fff",
		fontSize: 13,
		fontWeight: "500",
	},
	appleButton: {
		backgroundColor: "#fff",
		borderRadius: 4,
		paddingVertical: 4,
		paddingHorizontal: 12,
		marginHorizontal: 4,
		display: "inline-flex",
		borderWidth: 1,
		borderColor: "#0070c9",
		padding: 5,
	},
	appleButtonText: {
		color: "#0070c9",
		fontSize: 13,
		fontWeight: "600",
	},
	appleButtonContainer: {
		backgroundColor: "#fff",
		padding: 5,
		marginHorizontal: 4,
		borderRadius: 4,
	},
	setupItemContainer: {
		flexDirection: "column",
		gap: 16,
		marginBottom: 20,
	},
	setupItem: {
		flexDirection: "row",
		alignItems: "flex-start",
		marginBottom: 8,
	},
	setupItemIconContainer: {
		backgroundColor: "#0A84FF15",
		borderRadius: 8,
		padding: 8,
		marginTop: 2,
	},
	setupItemContent: {
		marginLeft: 12,
		flex: 1,
	},
	setupItemTitle: {
		color: "#fff",
		fontSize: 15,
		fontWeight: "600",
		marginBottom: 4,
	},
	setupItemDescription: {
		color: "#999",
		fontSize: 13,
		lineHeight: 18,
	},
	merchantIdContainer: {
		backgroundColor: "#222",
		borderRadius: 8,
		padding: 16,
		marginBottom: 20,
	},
	merchantIdLabel: {
		color: "#999",
		fontSize: 12,
		marginBottom: 12,
	},
	merchantIdPickerContainer: {
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		gap: 12,
		borderWidth: 1,
		borderColor: "#444",
	},
	merchantIdPicker: {
		flexDirection: "column",
		gap: 12,
	},
	merchantIdOption: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: 12,
		borderRadius: 8,
		borderWidth: 1,
		borderColor: "#444",
		marginBottom: 8,
		backgroundColor: "#333",
	},
	merchantIdOptionSelected: {
		backgroundColor: "#0A84FF15",
		borderColor: "#0A84FF",
	},
	merchantIdOptionText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	merchantIdOptionTextSelected: {
		color: "#0A84FF",
		fontWeight: "600",
	},
	merchantIdIdentifier: {
		color: "#999",
		fontSize: 12,
		fontFamily: "monospace",
		marginLeft: 8,
	},
	selectedMerchantDetails: {
		backgroundColor: "#33333350",
		borderRadius: 8,
		padding: 16,
		marginTop: 16,
	},
	selectedMerchantTitle: {
		color: "#fff",
		fontSize: 18,
		fontWeight: "600",
		marginBottom: 12,
	},
	selectedMerchantItem: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 8,
	},
	selectedMerchantLabel: {
		color: "#999",
		fontSize: 14,
		width: 100,
	},
	selectedMerchantValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
		flex: 1,
	},
	emptyMerchantContainer: {
		padding: 24,
		alignItems: "center",
		backgroundColor: "#33333350",
		borderRadius: 8,
	},
	emptyMerchantIcon: {
		marginBottom: 16,
	},
	emptyMerchantTitle: {
		color: "#fff",
		fontSize: 18,
		fontWeight: "600",
		marginBottom: 8,
	},
	emptyMerchantText: {
		color: "#999",
		fontSize: 14,
		textAlign: "center",
		lineHeight: 20,
	},
	orText: {
		color: "#999",
		fontSize: 13,
		fontWeight: "500",
		marginBottom: 12,
		textAlign: "center",
	},
	secondaryActionButton: {
		backgroundColor: "#0A84FF15",
		borderWidth: 1,
		borderColor: "#0A84FF",
	},
	secondaryActionButtonText: {
		color: "#0A84FF",
	},
	merchantIdCheckmark: {
		marginLeft: 8,
	},
	refreshButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 6,
		padding: 8,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	},
	refreshButtonText: {
		color: "#fff",
		fontSize: 13,
		fontWeight: "500",
	},
	merchantIdOptionContent: {
		flexDirection: "column",
		flex: 1,
	},
	merchantIdStatusContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	merchantIdStatus: {
		color: "#999",
		fontSize: 12,
		fontFamily: "monospace",
	},
	successContainer: {
		backgroundColor: "#82C91E15", // Light green background
		padding: 12,
		borderRadius: 8,
		marginBottom: 16,
	},
	successIcon: {
		marginBottom: 16,
	},
	successTitle: {
		fontSize: 24,
		fontWeight: "700",
		color: "#fff",
		marginBottom: 12,
		textAlign: "center",
	},
	successText: {
		color: "#82C91E", // Green text color
		fontSize: 14,
	},
	infoBox: {
		backgroundColor: "#0A84FF15",
		borderRadius: 8,
		padding: 12,
		marginBottom: 20,
	},
	infoBoxText: {
		color: "#0A84FF",
		fontSize: 13,
		lineHeight: 18,
	},
	linkText: {
		color: "#0A84FF",
		textDecorationLine: "underline",
		fontSize: 13,
		marginLeft: 4,
	},
	// Add new styles for Stripe Connect
	connectButton: {
		backgroundColor: "#0A84FF15",
	},
	disconnectButton: {
		backgroundColor: "#FF453A15",
	},
	connectInfoContainer: {
		marginTop: 24,
		width: "100%",
	},
	connectInfoItem: {
		flexDirection: "row",
		alignItems: "flex-start",
		marginBottom: 16,
	},
	connectInfoIcon: {
		backgroundColor: "#0A84FF15",
		borderRadius: 8,
		padding: 8,
	},
	connectInfoContent: {
		marginLeft: 12,
		flex: 1,
	},
	connectInfoTitle: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
		marginBottom: 4,
	},
	connectInfoDescription: {
		color: "#999",
		fontSize: 13,
		lineHeight: 18,
	},
	stripePubKeyContainer: {
		marginBottom: 20,
	},
	stripePubKeyHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 4,
	},
	stripePubKeyLabel: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	stripePubKeyInput: {
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
		borderWidth: 1,
		borderColor: "#444",
		marginBottom: 4,
	},
	stripePubKeyHelp: {
		color: "#999",
		fontSize: 12,
		marginBottom: 4,
	},
	stripePubKeyValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
	},
	savePublishableKeysButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 6,
		padding: 12,
		alignItems: "center",
	},
	savePublishableKeysButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	keysDivider: {
		height: 1,
		backgroundColor: "#444",
		marginVertical: 16,
	},
	configSummaryContainer: {
		marginBottom: 16,
	},
	configSummaryTitle: {
		color: "#fff",
		fontSize: 16,
		fontWeight: "600",
		marginBottom: 8,
	},
	configSummaryItem: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 4,
	},
	configSummaryLabel: {
		color: "#999",
		fontSize: 14,
		fontWeight: "500",
	},
	configSummaryValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
	},
});

export default PaymentsService;
